// Canonical artist-genre vocabulary. The data lives in genres.json so the
// enrich-artists resolver can append aliases and promoted genres without
// editing TypeScript; this file is the only reader. See
// plans/artist-genre-backfill.md.
//
// Families are never stored on artists — familyOf() derives them here, so
// editing the vocabulary regroups every artist with no migration.

import vocabulary from './genres.json'

export interface GenreEntry {
  label: string
  family: string
  aliases?: string[]
  /** false: the bare label is too ambiguous in prose ("country", "bass") — only its aliases match bios. */
  bioLabel?: boolean
}

export const GENRE_FAMILIES: readonly string[] = vocabulary.families
export const GENRES: readonly GenreEntry[] = vocabulary.genres

/** Too generic to show next to anything more specific. */
const GENERIC = new Set(['Electronic'])
export const MAX_GENRES = 3

/** Case, punctuation and `&`/`and`/`n` insensitive key. */
export function genreKey(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/(^|[\s-])'?n'?(?=[\s-])/g, '$1and')
    .replace(/[^a-z0-9]/g, '')
}

/** Key → entry for a genre list. Throws when two entries claim the same spelling. */
export function indexVocabulary(genres: readonly GenreEntry[]): Map<string, GenreEntry> {
  const index = new Map<string, GenreEntry>()
  for (const entry of genres) {
    for (const name of [entry.label, ...(entry.aliases ?? [])]) {
      const key = genreKey(name)
      const prior = index.get(key)
      if (prior && prior !== entry) {
        throw new Error(`genres.json: "${name}" maps to both ${prior.label} and ${entry.label}`)
      }
      index.set(key, entry)
    }
  }
  return index
}

const BY_KEY = indexVocabulary(GENRES)

export function genreEntry(raw: string): GenreEntry | null {
  return BY_KEY.get(genreKey(raw)) ?? null
}

export function canonicalGenre(raw: string): string | null {
  return genreEntry(raw)?.label ?? null
}

export function familyOf(raw: string): string | null {
  return genreEntry(raw)?.family ?? null
}

const ACRONYMS = new Set(['uk', 'us', 'dj', 'edm', 'idm', 'ebm', 'r&b', 'dnb', 'ukg'])

/** Display form for a genre outside the vocabulary. Keeps curated casing; title-cases lowercase input. */
function displayForm(raw: string): string {
  const trimmed = raw.trim().replace(/\s+/g, ' ')
  if (/[A-Z]/.test(trimmed)) return trimmed
  return trimmed.replace(/[^\s-]+/g, word =>
    ACRONYMS.has(word) ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1),
  )
}

/**
 * Canonical, deduped, display-ready genres: vocabulary labels where known,
 * title-cased otherwise, generic labels dropped when anything specific is
 * present, capped at MAX_GENRES.
 */
export function normalizeGenres(raw: readonly (string | null | undefined)[] | null | undefined): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const value of raw ?? []) {
    if (!value?.trim()) continue
    const label = canonicalGenre(value) ?? displayForm(value)
    const key = genreKey(label)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(label)
  }
  const specific = out.filter(g => !GENERIC.has(g))
  return (specific.length ? specific : out).slice(0, MAX_GENRES)
}

/** Comma-joined display text, or null when there is nothing to show. */
export function formatGenres(raw: readonly (string | null | undefined)[] | null | undefined): string | null {
  const genres = normalizeGenres(raw)
  return genres.length ? genres.join(', ') : null
}

// ── Bio extraction ────────────────────────────────────────────────────────

export type BioHitStrength = 'strong' | 'weak'

export interface BioGenreHit {
  label: string
  strength: BioHitStrength
  /** The sentence the genre was found in, for the classifier and for review. */
  quote: string
}

const BIO_IGNORE = new Set(vocabulary.bioIgnore.map(genreKey))

// Phrases where a genre word means something else. Blanked before matching.
const GUARDS = [
  /\b(?:in|open|full|opera|green|coffee|club|fun|warehouse)[\s-]house\b/gi,
  /\bhouse[\s-](?:part(?:y|ies)|band|of\s+\w+|guests?|rules?|show|concerts?)\b/gi,
  /\bthe\s+house\b(?!\s+(?:music|scene|dj|producer|sound))/gi,
  /\bpop[\s-]ups?\b/gi,
  /\bgarage[\s-]band\b/gi,
  /\blatin\s+america\w*\b/gi,
  /\bsoul[\s-](?:searching|mates?)\b/gi,
  /\bbody\s+and\s+soul\b/gi,
]

// "<genre> DJ", "<genre> producer", "the underground <genre> scene".
const ROLE = /^[\w\s,/&'-]{0,25}?\b(?:djs?|producers?|artists?|duo|trio|collective|act|band|selectors?|pioneers?|veterans?|heavyweights?|outfit|project|composers?|vocalists?|singers?|rappers?|musicians?|scene|sound|label)\b/i

function patternFor(name: string): string {
  return name
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\s*&\s*/g, '\\s*(?:&|and|n|\'n\')\\s*')
    .replace(/[\s-]+/g, '[\\s-]?')
}

const BIO_PATTERNS: { entry: GenreEntry; re: RegExp }[] = GENRES.flatMap(entry => {
  const names = [...(entry.bioLabel === false ? [] : [entry.label]), ...(entry.aliases ?? [])]
    .filter(n => !BIO_IGNORE.has(genreKey(n)))
  if (!names.length) return []
  const alternation = names.sort((a, b) => b.length - a.length).map(patternFor).join('|')
  return [{ entry, re: new RegExp(`(?<![\\w-])(?:${alternation})(?![\\w])`, 'gi') }]
})

function sentences(text: string): { text: string; start: number }[] {
  const out: { text: string; start: number }[] = []
  const re = /[^.!?\n]+[.!?]*/g
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m[0].trim()) out.push({ text: m[0].trim(), start: m.index })
  }
  return out
}

interface Span { entry: GenreEntry; start: number; end: number }

function spans(text: string): Span[] {
  let guarded = text
  for (const guard of GUARDS) guarded = guarded.replace(guard, m => ' '.repeat(m.length))
  const all: Span[] = []
  for (const { entry, re } of BIO_PATTERNS) {
    re.lastIndex = 0
    for (let m = re.exec(guarded); m; m = re.exec(guarded)) {
      all.push({ entry, start: m.index, end: m.index + m[0].length })
    }
  }
  // Longest match wins an overlap: "deep house" is not also "house".
  all.sort((a, b) => (b.end - b.start) - (a.end - a.start) || a.start - b.start)
  const kept: Span[] = []
  for (const s of all) if (!kept.some(k => s.start < k.end && k.start < s.end)) kept.push(s)
  return kept.sort((a, b) => a.start - b.start)
}

/**
 * Genres a bio names, each marked strong or weak. Strong: in the short bio, in
 * the bio's first sentence, or followed closely by a role word ("dubstep
 * producer", "the hard techno scene"). Weak: anywhere else — usually a list of
 * influences, which is why weak hits never decide anything on their own.
 */
export function extractBioGenres(shortBio?: string | null, bio?: string | null): BioGenreHit[] {
  const best = new Map<string, BioGenreHit>()
  const add = (label: string, strength: BioHitStrength, quote: string) => {
    const prior = best.get(label)
    if (!prior || (prior.strength === 'weak' && strength === 'strong')) {
      best.set(label, { label, strength, quote: quote.slice(0, 240) })
    }
  }

  for (const [source, text] of [['short', shortBio], ['bio', bio]] as const) {
    if (!text?.trim()) continue
    const parts = sentences(text)
    for (const span of spans(text)) {
      const sentence = parts.find(p => span.start >= p.start && span.start < p.start + p.text.length + 1)
      const quote = sentence?.text ?? text.slice(Math.max(0, span.start - 80), span.end + 80)
      const isLead = source === 'short' || sentence === parts[0]
      const hasRole = ROLE.test(text.slice(span.end, span.end + 60))
      add(span.entry.label, isLead || hasRole ? 'strong' : 'weak', quote)
    }
  }
  return [...best.values()]
}

/**
 * The genres a bio settles without the classifier, or null when it needs
 * judgment. Settles only when the strong hits are few and agree: one to three
 * specific labels spanning at most two families. A bare "Electronic" never
 * settles — the classifier should find something more specific.
 */
export function bioDecision(hits: readonly BioGenreHit[]): string[] | null {
  const strong = hits.filter(h => h.strength === 'strong' && !GENERIC.has(h.label)).map(h => h.label)
  if (strong.length === 0 || strong.length > MAX_GENRES) return null
  const families = new Set(strong.map(familyOf))
  return families.size <= 2 ? strong : null
}
